import React, { useContext } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import AuthContext from 'contexts/authContext';
import AppContext from 'contexts/appContext';
import AppHeader from './header/appHeader';
import BasicHeader from './header/basicHeader';
import Flow from 'components/common/flow/flow';
import PermissionsService from 'services/permissionsService';

/* ERRORS */
import ErrorPageNotFound from './errors/errorPageNotFound';

/* TESTS */
import ComponentTestPage from 'components/navigation/tests/componentTestPage';
import DataTestPage from 'components/navigation/tests/dataTestPage';
import FormTestPage from 'components/navigation/tests/formTestPage';
import ModalTestPage from 'components/navigation/tests/modalTestPage';
import PlaygroundPage from 'components/navigation/tests/playgroundPage';

/* DEMOS */
import LighthouseEnterpriseDemoPage from 'components/navigation/demos/lighthouseEnterpriseDemoPage';

/* AUTH */
import LoginPage from 'components/navigation/auth/login/loginPage';
import LoginViaPortal from 'components/navigation/auth/loginviaportal/loginViaPortal';
import LoginViaToken from 'components/navigation/auth/loginviatoken/loginViaToken';
import LoginPortal from 'components/navigation/auth/loginPortal/loginPortal';

/* PAGES */
import HomePage from 'components/navigation/home/homePage';
import AccountManagePage from 'components/navigation/account/accountManagePage';
import AccountSettingsPage from 'components/navigation/account/accountSettingsPage';
import SoftwareUpdatePage from 'components/navigation/softwareUpdate/softwareUpdatePage';
import SoftwareUpdateEditPage from 'components/navigation/softwareUpdate/edit/softwareUpdateEditPage';
import SoftwareUpdateCreatePage from 'components/navigation/softwareUpdate/create/softwareUpdateCreatePage';
import SoftwareUpdateDetailsPage from 'components/navigation/softwareUpdate/details/softwareUpdateDetailsPage';
import SoftwareUpdateDashboardPage from 'components/navigation/softwareUpdate/dashboard/softwareUpdateDashboardPage';
import SiteManagerPage from 'components/navigation/siteManager/siteManagerPage';
import SiteAttributeViewerPage from 'components/navigation/siteAttributeViewer/siteAttributeViewerPage';
import BackofficeReportsRunPage from 'components/navigation/backofficeReports/run/backofficeReportsRunPage';
import BackofficeReportsTaskPage from 'components/navigation/backofficeReports/task/details/backofficeReportsTaskPage';
import BackofficeReportsTaskEditPage from 'components/navigation/backofficeReports/task/edit/backofficeReportsTaskEditPage';
import BackofficeReportsStoreTasksPage from 'components/navigation/backofficeReports/tasks/backofficeReportsStoreTasksPage';
import BackofficeReportsTaskCreatePage from 'components/navigation/backofficeReports/task/create/backofficeReportsTaskCreatePage';
import BoardingDashboardPage from 'components/navigation/boarding/dashboard/boardingDashboardPage';
import BoardingWizardPage from 'components/navigation/boarding/wizard/boardingWizardPage';
import BoardingConfirmationPage from 'components/navigation/boarding/order/confirmation/boardingConfirmationPage';
import BoardingOrderStatusPage from 'components/navigation/boarding/order/details/boardingOrderDetailsPage';
import OrdersPage from 'components/navigation/orders/ordersPage';
import OrderDetailsPage from 'components/navigation/orders/details/orderDetailsPage';
import OrdersCreatePage from 'components/navigation/orders/create/ordersCreatePage';
import DashboardPage from 'components/navigation/dashboard/dashboardPage';
import MenusPage from 'components/navigation/menus/menusPage';
import MenusScreenLayoutPage from 'components/navigation/menus/screens/menusScreenLayoutPage';
import MenusCellDetailsPage from 'components/navigation/menus/cells/menusCellDetailsPage';
import EmployeesPage from 'components/navigation/employees/employeesPage';
import EmployeesDetailsPage from 'components/navigation/employees/details/employeesDetailsPage';
import UsersPage from 'components/navigation/users/usersPage';
import UserDetailsPage from 'components/navigation/users/details/userDetailsPage';

/* OLD PAGES */
import MenuUpdatePage from 'components/navigation_old/menuUpdate/menuUpdatePage';
import SystemStatusPage from 'components/navigation_old/systemStatus/systemStatusPage';
import SystemStatusDetailsPage from 'components/navigation_old/systemStatus/details/systemStatusPageDetails';
import SoftwareUpdateManifestPage from 'components/navigation_old/softwareUpdateManifest/softwareUpdateManifestPage';
import TimeAndAttendancePage from 'components/navigation_old/timeAndAttendance/timeAndAttendance';
import MyViewsPage from 'components/navigation_old/myViews/myViewsPage';
import EmployeeAddPage from 'components/navigation_old/employees/employeeAdd/employeeAdd';
import EmployeeEditPage from 'components/navigation_old/employees/employeeEdit/employeeEdit';
import EmployeeDetailsPage from 'components/navigation_old/employees/employeeDetails/employeeDetails';
import EmployeeListPage from 'components/navigation_old/employees/employeeList/employeeList';

/* CONTEXTS */
import { BoardingContextProvider } from 'components/navigation/boarding/boardingContext';

export default function AppRoutes() {
  const { authIsValid } = useContext(AuthContext);

  if (authIsValid()) {
    //Authenticated routes
    return (
      <>
        <Switch>
          <AuthRoute path='/Login' component={LoginPage} />
          <Route path='/Logout' render={(props) => <Logout {...props} />} />
          <Route path='/LoginViaPortal' render={(props) => <LoginViaPortal {...props} />} />
          <Route path='/LoginViaToken' render={(props) => <LoginViaToken {...props} />} />
          <Route path='/LoginPortal' render={(props) => <LoginPortal {...props} />} />
          <AuthRoute exact path='/Developers/Test/Components' component={ComponentTestPage} title='Components' />
          <AuthRoute exact path='/Developers/Test/Data' component={DataTestPage} title='Data' />
          <AuthRoute exact path='/Developers/Test/Forms' component={FormTestPage} title='Forms' />
          <AuthRoute exact path='/Developers/Test/Modals' component={ModalTestPage} title='Modals' />
          <AuthRoute exact path='/Developers/Playground' component={PlaygroundPage} title='Playground' />
          <Route path='/Developers/Demo/Lighthouse' component={LighthouseEnterpriseDemoPage} />

          <NavRoute exact path='/Account/Manage' component={AccountManagePage} area={PermissionsService.enums.AREAS.Account} />
          <NavRoute exact path='/Account/Settings' component={AccountSettingsPage} area={PermissionsService.enums.AREAS.Account} />
          <NavRoute exact path='/MenuUpdate' component={MenuUpdatePage} area={PermissionsService.enums.AREAS.MenuUpdate} />
          <NavRoute exact path='/SoftwareUpdate' component={SoftwareUpdatePage} area={PermissionsService.enums.AREAS.SoftwareUpdate} />
          <NavRoute exact path='/SoftwareUpdate/Dashboard' component={SoftwareUpdateDashboardPage} area={PermissionsService.enums.AREAS.SoftwareUpdate} />
          <NavRoute exact path='/SoftwareUpdate/Create' component={SoftwareUpdateCreatePage} area={PermissionsService.enums.AREAS.SoftwareUpdate} />
          <NavRoute path='/SoftwareUpdate/Edit/:id' component={SoftwareUpdateEditPage} area={PermissionsService.enums.AREAS.SoftwareUpdate} />
          <NavRoute path='/SoftwareUpdate/:id' component={SoftwareUpdateDetailsPage} area={PermissionsService.enums.AREAS.SoftwareUpdate} />
          <NavRoute path='/SoftwareUpdateManifest/:id' component={SoftwareUpdateManifestPage} area={PermissionsService.enums.AREAS.SoftwareUpdate} />
          <NavRoute exact path='/BackofficeReports/Run' component={BackofficeReportsRunPage} area={PermissionsService.enums.AREAS.BackofficeReports} />
          <NavRoute exact path='/BackofficeReports/Tasks' component={BackofficeReportsStoreTasksPage} area={PermissionsService.enums.AREAS.BackofficeReports} />
          <NavRoute exact path='/BackofficeReports/Task/Create' component={BackofficeReportsTaskCreatePage} area={PermissionsService.enums.AREAS.BackofficeReports} />
          <NavRoute path='/BackofficeReports/Task/Edit/:id' component={BackofficeReportsTaskEditPage} area={PermissionsService.enums.AREAS.BackofficeReports} />
          <NavRoute path='/BackofficeReports/Task/:id/' component={BackofficeReportsTaskPage} area={PermissionsService.enums.AREAS.BackofficeReports} />
          <NavRoute exact path='/Orders' component={OrdersPage} area={PermissionsService.enums.AREAS.Orders} />
          <NavRoute exact path='/Orders/Create' component={OrdersCreatePage} area={PermissionsService.enums.AREAS.Orders} />
          <NavRoute path='/Orders/:id' component={OrderDetailsPage} area={PermissionsService.enums.AREAS.Orders} />
          <NavRoute exact path='/Dashboard' component={DashboardPage} area={PermissionsService.enums.AREAS.SiteDashboard} />
          <NavRoute exact path='/Menus' component={MenusPage} area={PermissionsService.enums.AREAS.Menus} />
          <NavRoute path='/Menus/Screen/:id' component={MenusScreenLayoutPage} area={PermissionsService.enums.AREAS.Menus} />
          <NavRoute path='/Menus/Cell/:id' component={MenusCellDetailsPage} area={PermissionsService.enums.AREAS.Menus} />
          <NavRoute exact path='/Employees' component={EmployeesPage} area={PermissionsService.enums.AREAS.Employees} />
          <NavRoute path='/Employees/:id' component={EmployeesDetailsPage} area={PermissionsService.enums.AREAS.Employees} />
          <NavRoute exact path='/Users' component={UsersPage} area={PermissionsService.enums.AREAS.Employees} />
          <NavRoute path='/Users/:id' component={UserDetailsPage} area={PermissionsService.enums.AREAS.Employees} />
          <NavRoute exact path='/Boarding/Dashboard' component={BoardingDashboardPage} area={PermissionsService.enums.AREAS.Boarding} />
          <Route path='/Boarding' component={BoardingSwitch} />

          <NavRoute exact path='/' component={HomePage} area={PermissionsService.enums.AREAS.Home} />

          <NavRoute exact path='/SiteManager' component={SiteManagerPage} />
          <NavRoute exact path='/SiteAttributeViewer' component={SiteAttributeViewerPage} />
          <NavRoute exact path='/SystemStatus' component={SystemStatusPage} />
          <NavRoute exact path='/SystemStatus/Details/:id/' component={SystemStatusDetailsPage} />
          <NavRoute exact path='/Views/MyViews' component={MyViewsPage} />
          <NavRoute exact path='/EmployeeList' component={EmployeeListPage} />
          <NavRoute exact path='/EmployeeList/Add/' component={EmployeeAddPage} />
          <NavRoute path='/EmployeeList/:id/edit' component={EmployeeEditPage} />
          <NavRoute path='/EmployeeList/:id/' component={EmployeeDetailsPage} />
          <NavRoute exact path='/TimeAndAttendance' component={TimeAndAttendancePage} />

          <Route component={ErrorPageNotFound} />
        </Switch>
      </>
    );
  }
  else {
    //Unauthenticated routes
    return (
      <Switch>
        <AuthRoute path='/Login' component={LoginPage} />
        <Route path='/Logout' render={(props) => <Logout {...props} />} />
        <Route path='/LoginViaPortal' render={(props) => <LoginViaPortal {...props} />} />
        <Route path='/LoginViaToken' render={(props) => <LoginViaToken {...props} />} />
        <Route path='/LoginPortal' render={(props) => <LoginPortal {...props} />} />
        <AuthRoute exact path='/Developers/Test/Components' component={ComponentTestPage} title='Components' />
        <AuthRoute exact path='/Developers/Test/Data' component={DataTestPage} title='Data' />
        <AuthRoute exact path='/Developers/Test/Forms' component={FormTestPage} title='Forms' />
        <AuthRoute exact path='/Developers/Test/Modals' component={ModalTestPage} title='Modals' />
        <AuthRoute exact path='/Developers/Playground' component={PlaygroundPage} title='Playground' />
        <Route path='/Developers/Demo/Lighthouse' component={LighthouseEnterpriseDemoPage} />

        <Route render={(props) => <RedirectToLogin {...props} />} />
      </Switch>
    );
  }
}

function Logout(props) {
  const { setAuthLoggedOut } = useContext(AuthContext);
  const { setAppLoggedOut } = useContext(AppContext);

  setAuthLoggedOut();
  setAppLoggedOut();

  return <Redirect to={{ pathname: '/Login' }} {...props} />;
}

function RedirectToLogin(props) {
  const redirect = {
    pathname: '/Login',
    state: {
      from: props.location,
    },
  };
  return <Redirect {...props} to={redirect} />;
}

function NavRoute({ component: Component, title, area, ...props }) {
  const rights = PermissionsService.getAreaPermissions(area);
  if (rights.isViewAllowed) {
    const render = (renderProps) => {
      return (
        <Flow className='h-100'>
          <Flow.Header>
            <AppHeader />
          </Flow.Header>
          <Flow.Body>
            <Component {...renderProps} {...props} rights={rights} />
          </Flow.Body>
        </Flow>
      );
    };
    return <Route render={render} {...props} />;
  }
  else {
    return <Route component={ErrorPageNotFound} />;
  }
}

function AuthRoute({ component: Component, title, ...props }) {
  const render = (renderProps) => {
    return (
      <Flow className='h-100'>
        <Flow.Header>
          <BasicHeader title={title || 'Login'} />
        </Flow.Header>
        <Flow.Body>
          <Component {...renderProps} {...props} />
        </Flow.Body>
      </Flow>
    );
  };
  return <Route render={render} {...props} />;
}

function BoardingSwitch() {
  return (
    <BoardingContextProvider>
      <Switch>
        <NavRoute exact path='/Boarding/Wizard' component={BoardingWizardPage} area={PermissionsService.enums.AREAS.Boarding} />
        <NavRoute exact path='/Boarding/Confirmation/:id' component={BoardingConfirmationPage} area={PermissionsService.enums.AREAS.Boarding} />
        <NavRoute exact path='/Boarding/Order/:id' component={BoardingOrderStatusPage} area={PermissionsService.enums.AREAS.Boarding} />

        <Route component={ErrorPageNotFound} />
      </Switch>
    </BoardingContextProvider>
  );
}